'use client';

import React, { useMemo, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { runScrollTriggerSetup } from 'utils/performanceLabScrollTrigger';
import { SectionBg, SectionContent } from './atoms';
import {
  FixedBgContent,
  FixedBgImageWrap,
  FixedBgLayer,
  FixedBgOverlay,
  FixedBgSectionRoot,
  FixedBgTextStage,
  FixedBgVhSpacer,
} from './fixedBackgroundScroll';
import type { ResponsiveParallaxBg } from './plLandingAssets';
import {
  ParallaxBackgroundImg,
  ResponsiveParallaxBackgroundImages,
  resolveParallaxBgUrl,
} from './ResponsiveParallaxBackgroundImages';
import {
  createWordOpacityUpdater,
  getWordElements,
  isWhitespaceToken,
  splitTextIntoTokens,
  WORD_REVEAL_CLASS,
} from './wordReveal';
import { PL_BOOK_TOUR_SCROLL_ANCHOR_ATTR } from './performanceLabBookTourTypeform';
import styles from './TextRevealSection.module.css';

gsap.registerPlugin(ScrollTrigger);

const REVEAL_TO_OPACITY = 1;
const DEFAULT_SCROLL_SCRUB = 0.6;
const DEFAULT_SCROLL_TRACK_VH_MOBILE = 2;
const DEFAULT_SCROLL_TRACK_VH_DESKTOP = 3;
const DEFAULT_SCROLL_ENTER_VH_MOBILE = 0.5;
const DEFAULT_SCROLL_ENTER_VH_DESKTOP = 0.5;
const DEFAULT_SCROLL_EXIT_VH_MOBILE = 0.75;
const DEFAULT_SCROLL_EXIT_VH_DESKTOP = 1;
const DEFAULT_REVEAL_FROM_OPACITY = 0.24;
const DEFAULT_BACKGROUND_COLOR = '#000';
const DEFAULT_TEXT_COLOR = '#fff';

const DEFAULT_TEXT_PADDING_X_MOBILE = '24px';
const DEFAULT_TEXT_PADDING_X_DESKTOP = '32px';

export type TextRevealSectionProps = {
  ariaLabel: string;
  bodyText?: string;
  /** Background image only — no copy or word-reveal animation. */
  imageOnly?: boolean;
  scrollTrackVhMobile?: number;
  scrollTrackVhDesktop?: number;
  scrollEnterVhMobile?: number;
  scrollEnterVhDesktop?: number;
  scrollExitVhMobile?: number;
  scrollExitVhDesktop?: number;
  revealFromOpacity?: number;
  scrollScrub?: number;
  textPaddingXMobile?: string;
  textPaddingXDesktop?: string;
  /** Max width of the reveal text block. Defaults to `1273px`. */
  textMaxWidth?: string;
  collapseLineBreaksOnMobile?: boolean;
  backgroundColor?: string;
  /** Reveal text color (defaults to white for dark sections). */
  textColor?: string;
  /** Single image (legacy). Prefer `backgroundImages` for mobile full / desktop min. */
  backgroundImage?: string;
  backgroundImages?: ResponsiveParallaxBg;
  backgroundOverlay?: string;
  clipOverflow?: boolean;
  fixedBackgroundScroll?: boolean;
  /** Marks this section as the auto book-tour popup scroll anchor. */
  bookTourScrollAnchor?: boolean;
};

const DEFAULT_MD_MAX_WIDTH = '768px';

function bodyTextToLines(
  bodyText: string,
  collapseLineBreaksOnMobile: boolean,
  isMobile: boolean,
): string[] {
  const text =
    collapseLineBreaksOnMobile && isMobile
      ? bodyText.replace(/\n/g, ' ')
      : bodyText;

  if (text.includes('\n')) {
    return text.split('\n').filter((line) => line.length > 0);
  }

  return [text];
}

function renderWordTokens(
  tokens: string[],
  lineKey: string,
  fromOpacity: number,
) {
  return tokens.map((token, index) => {
    if (isWhitespaceToken(token)) {
      return token;
    }

    return (
      <span
        key={`${lineKey}-word-${index}-${token}`}
        className={`${styles.word} ${WORD_REVEAL_CLASS}`}
        style={{ ['--word-opacity']: fromOpacity } as React.CSSProperties}
        aria-hidden="true"
      >
        {token}
      </span>
    );
  });
}

function renderRevealText(
  lineTokens: string[][],
  revealFromOpacity: number,
  textRef: React.RefObject<HTMLParagraphElement | null>,
  ariaText: string,
  textPaddingXMobile: string,
  textPaddingXDesktop: string,
  textColor: string,
  textMaxWidth: string,
) {
  return (
    <p
      ref={textRef}
      className={styles.text}
      aria-label={ariaText}
      style={
        {
          ['--text-pad-x-mobile']: textPaddingXMobile,
          ['--text-pad-x-desktop']: textPaddingXDesktop,
          ['--text-color']: textColor,
          ['--text-max-width']: textMaxWidth,
        } as React.CSSProperties
      }
    >
      {lineTokens.map((tokens, lineIndex) => (
        <React.Fragment key={`line-${lineIndex}`}>
          {lineIndex > 0 && <br />}
          {renderWordTokens(tokens, `line-${lineIndex}`, revealFromOpacity)}
        </React.Fragment>
      ))}
    </p>
  );
}

function renderParallaxImageNodes(
  backgroundImage: string | undefined,
  backgroundImages: ResponsiveParallaxBg | undefined,
) {
  if (backgroundImages) {
    return <ResponsiveParallaxBackgroundImages src={backgroundImages} />;
  }

  if (backgroundImage) {
    return (
      <ParallaxBackgroundImg
        src={resolveParallaxBgUrl(backgroundImage)}
        alt=""
        decoding="async"
      />
    );
  }

  return null;
}

function renderFixedBackgroundImage(
  backgroundColor: string,
  backgroundImage: string | undefined,
  backgroundImages: ResponsiveParallaxBg | undefined,
  backgroundOverlay: string | undefined,
) {
  const imageNodes = renderParallaxImageNodes(backgroundImage, backgroundImages);
  if (!imageNodes) return null;

  return (
    <FixedBgLayer aria-hidden>
      <FixedBgImageWrap $fallback={backgroundColor}>{imageNodes}</FixedBgImageWrap>
      {backgroundOverlay ? (
        <FixedBgOverlay
          aria-hidden
          style={{ background: backgroundOverlay }}
        />
      ) : null}
    </FixedBgLayer>
  );
}

export const TextRevealSection: React.FC<TextRevealSectionProps> = ({
  ariaLabel,
  bodyText = '',
  imageOnly = false,
  scrollTrackVhMobile = DEFAULT_SCROLL_TRACK_VH_MOBILE,
  scrollTrackVhDesktop = DEFAULT_SCROLL_TRACK_VH_DESKTOP,
  scrollEnterVhMobile = DEFAULT_SCROLL_ENTER_VH_MOBILE,
  scrollEnterVhDesktop = DEFAULT_SCROLL_ENTER_VH_DESKTOP,
  scrollExitVhMobile = DEFAULT_SCROLL_EXIT_VH_MOBILE,
  scrollExitVhDesktop = DEFAULT_SCROLL_EXIT_VH_DESKTOP,
  revealFromOpacity = DEFAULT_REVEAL_FROM_OPACITY,
  scrollScrub = DEFAULT_SCROLL_SCRUB,
  textPaddingXMobile = DEFAULT_TEXT_PADDING_X_MOBILE,
  textPaddingXDesktop = DEFAULT_TEXT_PADDING_X_DESKTOP,
  textMaxWidth = '1273px',
  backgroundColor = DEFAULT_BACKGROUND_COLOR,
  textColor = DEFAULT_TEXT_COLOR,
  backgroundImage,
  backgroundImages,
  backgroundOverlay,
  clipOverflow = false,
  collapseLineBreaksOnMobile = false,
  fixedBackgroundScroll = false,
  bookTourScrollAnchor = false,
}) => {
  const sectionRef = useRef<HTMLElement>(null);
  const scrollTrackRef = useRef<HTMLDivElement>(null);
  const pinZoneRef = useRef<HTMLDivElement>(null);
  const textStageRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(max-width: ${DEFAULT_MD_MAX_WIDTH})`).matches;
  });
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  const totalSectionVhMobile =
    scrollEnterVhMobile + scrollTrackVhMobile + scrollExitVhMobile;
  const totalSectionVhDesktop =
    scrollEnterVhDesktop + scrollTrackVhDesktop + scrollExitVhDesktop;

  useIsomorphicLayoutEffect(() => {
    const mediaQuery = window.matchMedia(
      `(max-width: ${DEFAULT_MD_MAX_WIDTH})`,
    );
    const update = () => setIsMobile(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  useIsomorphicLayoutEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReducedMotion(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  const lines = useMemo(
    () =>
      imageOnly
        ? []
        : bodyTextToLines(bodyText, collapseLineBreaksOnMobile, isMobile),
    [bodyText, collapseLineBreaksOnMobile, imageOnly, isMobile],
  );

  const lineTokens = useMemo(
    () => lines.map((line) => splitTextIntoTokens(line)),
    [lines],
  );

  const ariaText = bodyText.replace(/\n/g, ' ');
  const wordFromOpacity = prefersReducedMotion
    ? REVEAL_TO_OPACITY
    : revealFromOpacity;

  useIsomorphicLayoutEffect(() => {
    if (imageOnly || fixedBackgroundScroll || prefersReducedMotion) return;

    return runScrollTriggerSetup({
      debugLabel: 'TextRevealSection sticky scroll',
      isReady: () =>
        Boolean(scrollTrackRef.current) &&
        Boolean(textRef.current) &&
        getWordElements(textRef.current).length > 0,
      setup: () => {
        const track = scrollTrackRef.current;
        const text = textRef.current;
        if (!track || !text) return;

        const words = getWordElements(text);
        if (!words.length) return;

        const applyWordOpacities = createWordOpacityUpdater(
          words,
          revealFromOpacity,
          REVEAL_TO_OPACITY,
        );

        applyWordOpacities(0);

        const trigger = ScrollTrigger.create({
          trigger: track,
          start: 'top top',
          end: 'bottom bottom',
          scrub: scrollScrub,
          invalidateOnRefresh: true,
          onUpdate: (self) => applyWordOpacities(self.progress),
        });

        return {
          cleanup: () => {
            trigger.kill();
          },
        };
      },
    });
  }, [
    bodyText,
    fixedBackgroundScroll,
    revealFromOpacity,
    scrollScrub,
    imageOnly,
    lines,
    prefersReducedMotion,
  ]);

  useIsomorphicLayoutEffect(() => {
    if (imageOnly || !fixedBackgroundScroll || prefersReducedMotion) return;

    return runScrollTriggerSetup({
      debugLabel: 'TextRevealSection fixed background scroll',
      isReady: () =>
        Boolean(pinZoneRef.current) &&
        Boolean(textStageRef.current) &&
        Boolean(textRef.current) &&
        getWordElements(textRef.current).length > 0,
      setup: () => {
        const pinZone = pinZoneRef.current;
        const textStage = textStageRef.current;
        const text = textRef.current;
        if (!pinZone || !textStage || !text) return;

        const words = getWordElements(text);
        if (!words.length) return;

        const applyWordOpacities = createWordOpacityUpdater(
          words,
          revealFromOpacity,
          REVEAL_TO_OPACITY,
        );

        applyWordOpacities(0);

        const trigger = ScrollTrigger.create({
          trigger: pinZone,
          start: 'top top',
          end: 'bottom bottom',
          pin: textStage,
          scrub: scrollScrub,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: (self) => applyWordOpacities(self.progress),
        });

        return {
          cleanup: () => {
            trigger.kill();
          },
        };
      },
    });
  }, [
    bodyText,
    fixedBackgroundScroll,
    revealFromOpacity,
    scrollScrub,
    lines,
    scrollTrackVhMobile,
    scrollTrackVhDesktop,
    scrollEnterVhMobile,
    scrollEnterVhDesktop,
    scrollExitVhMobile,
    scrollExitVhDesktop,
    imageOnly,
    prefersReducedMotion,
  ]);

  const stickyImageNodes = renderParallaxImageNodes(
    backgroundImage,
    backgroundImages,
  );

  const background = stickyImageNodes ? (
    <div
      className={styles.backgroundLayer}
      style={{ ['--bg-fallback']: backgroundColor } as React.CSSProperties}
      aria-hidden
    >
      <div className={styles.backgroundImageWrap}>{stickyImageNodes}</div>
      {backgroundOverlay ? (
        <div
          className={styles.backgroundOverlay}
          aria-hidden
          style={{ background: backgroundOverlay }}
        />
      ) : null}
    </div>
  ) : (
    <SectionBg
      className={styles.solidBackground}
      style={{ ['--solid-bg']: backgroundColor } as React.CSSProperties}
      aria-hidden
    />
  );

  const revealText = imageOnly
    ? null
    : renderRevealText(
        lineTokens,
        wordFromOpacity,
        textRef,
        ariaText,
        textPaddingXMobile,
        textPaddingXDesktop,
        textColor,
        textMaxWidth,
      );

  const scrollAnchorProps = bookTourScrollAnchor
    ? { 'data-pl-section': PL_BOOK_TOUR_SCROLL_ANCHOR_ATTR }
    : {};

  if (fixedBackgroundScroll) {
    return (
      <FixedBgSectionRoot
        ref={sectionRef}
        role="region"
        aria-label={ariaLabel}
        $mobileVh={totalSectionVhMobile}
        $desktopVh={totalSectionVhDesktop}
        $fallbackBg={backgroundColor}
        $clipOverflow={clipOverflow}
        {...scrollAnchorProps}
      >
        {renderFixedBackgroundImage(
          backgroundColor,
          backgroundImage,
          backgroundImages,
          backgroundOverlay,
        )}
        <FixedBgContent>
          <FixedBgVhSpacer
            $mobileVh={scrollEnterVhMobile}
            $desktopVh={scrollEnterVhDesktop}
            aria-hidden
          />
          <div
            className={styles.scrollTrack}
            ref={pinZoneRef}
            style={
              {
                ['--scroll-mobile-vh']: scrollTrackVhMobile,
                ['--scroll-desktop-vh']: scrollTrackVhDesktop,
              } as React.CSSProperties
            }
          >
            <FixedBgTextStage ref={textStageRef} aria-hidden={imageOnly}>
              {!imageOnly && (
                <SectionContent $centered>{revealText}</SectionContent>
              )}
            </FixedBgTextStage>
          </div>
          <FixedBgVhSpacer
            $mobileVh={scrollExitVhMobile}
            $desktopVh={scrollExitVhDesktop}
            aria-hidden
          />
        </FixedBgContent>
      </FixedBgSectionRoot>
    );
  }

  const stickyBackground =
    backgroundImage || backgroundImages ? undefined : backgroundColor;

  return (
    <section
      ref={sectionRef}
      className={styles.sectionRoot}
      role="region"
      aria-label={ariaLabel}
      data-clip-overflow={clipOverflow ? 'true' : undefined}
      style={{ ['--section-bg']: backgroundColor } as React.CSSProperties}
      {...scrollAnchorProps}
    >
      <div
        className={styles.scrollTrack}
        ref={scrollTrackRef}
        style={
          {
            ['--scroll-mobile-vh']: scrollTrackVhMobile,
            ['--scroll-desktop-vh']: scrollTrackVhDesktop,
          } as React.CSSProperties
        }
      >
        <div
          className={styles.stickyViewport}
          style={
            stickyBackground
              ? ({ ['--sticky-bg']: stickyBackground } as React.CSSProperties)
              : undefined
          }
        >
          {background}
          {!imageOnly && (
            <SectionContent $centered>{revealText}</SectionContent>
          )}
        </div>
      </div>
    </section>
  );
};
